import { CarFormData } from "@/types/carFormData"
import { extractDriveFileId } from "@/utils/helpers"
import { CarCreated } from "../../api/models/cars"
import {
  createCliente,
  createClientePayload,
  editCliente,
  getClientes,
} from "./apiClientes"
import {
  deleteFile,
  deleteFolder,
  getFilesFromDriveFolder,
  uploadFiles,
} from "./apiDriveImages"

export async function getCars(
  pageNumber: number,
  limitNumber: number,
  filters: Record<string, any> = {}
) {
  try {
    // Convert the filters object to a query string
    const filterParams = new URLSearchParams({
      filters: JSON.stringify(filters),
    }).toString()

    // Construct the URL with the page, limit, and filters
    const urlWithParams = `/.netlify/functions/get_cars?page=${pageNumber}&limit=${limitNumber}${filterParams ? "&" + filterParams : ""}`

    // Fetch the data from the API
    const response = await fetch(urlWithParams)
    // Check if the response is ok (status code 200-299)
    if (!response.ok) {
      throw new Error("Failed to fetch cars")
    }

    // Return the parsed JSON data
    return response.json()
  } catch (error) {
    console.error(error)
    throw new Error("Cars could not be loaded")
  }
}

export async function getCarById(id: string) {
  const urlWithParams = `/.netlify/functions/get_car_by_id?id=${id}`

  try {
    const response = await fetch(urlWithParams)
    return response.json()
  } catch (error) {
    console.error(error)
    throw new Error("Cars could no be loaded")
  }
}

export async function getLastCars() {
  try {
    const response = await fetch("/.netlify/functions/get_last_cars")
    return response.json()
  } catch (error) {
    console.error(error)
    throw new Error("Cars could no be loaded")
  }
}

// The editCar function with explicit type
export async function editCar(
  id: string,
  carData: CarFormData,
  imagesToDelete: string[],
  imagesToUpload: {
    id: string
    url: string
    file: File
    source: string
    isCover?: boolean | undefined
  }[]
): Promise<CarCreated> {
  const urlWithParams = `/.netlify/functions/edit_car?id=${id}` // URL for your Netlify function

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms))

  const deleteFilesWithDelay = async (fileId: string) => {
    try {
      await deleteFile(fileId)
    } catch (error) {
      console.error(`Failed to delete file with ID ${fileId}:`, error)
    }
  }

  const deleteFilesInBatches = async (
    imagesToDelete: string[],
    delayMs: number
  ) => {
    for (let i = 0; i < imagesToDelete.length; i++) {
      await deleteFilesWithDelay(extractDriveFileId(imagesToDelete[i]))
      if (i < imagesToDelete.length - 1) {
        console.log(
          `Waiting for ${delayMs / 1000} seconds before next delete...`
        )
        await delay(delayMs) // Add delay between requests
      }
    }
  }

  if (imagesToDelete.length > 0) {
    await deleteFilesInBatches(imagesToDelete, 1000) // 1000ms = 1 second delay
  }

  if (imagesToUpload.length > 0) {
    const newImages = imagesToUpload.map((imgObject) => imgObject.file)
    const filesIndex =
      carData.images!.length + imagesToDelete.length - newImages.length
    // Step 1: Upload image files to Google Drive via your Netlify function
    await uploadFiles(newImages, carData._id!.toString(), filesIndex.toString())
  }
  
  const portadaIndex = parseInt(carData.portadaUrl ?? "0", 10)

  const uploadedFilesUrl = await getFilesFromDriveFolder(carData.folderId!)
  const images = uploadedFilesUrl.files.map((file: any) => file.publicUrl)

  const orderCarDataImages = new Map<string, number>();
  (carData.images as {url: string}[]).forEach((image, index) => {
    orderCarDataImages.set(image.url, index)
  })

  const sortedImages = [... images].sort((a,b) => {
    const aIndex = orderCarDataImages.has(a) ? orderCarDataImages.get(a)! : Infinity
    const bIndex = orderCarDataImages.has(b) ? orderCarDataImages.get(b)! : Infinity
    return aIndex - bIndex
  })

  carData.images = sortedImages
  // Conver portadaUrl to a number
  carData.portadaUrl = sortedImages[portadaIndex]

  if (carData.clienteId) {
    const { nombre, celular, email } = carData
    const clientePayload = { nombre, celular, email }
    await editCliente(carData.clienteId?._id.toString(), clientePayload)
    // now you can use nombre, celular, email
  }

  try {
    const response = await fetch(urlWithParams, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(carData), // Pass the car data to update
    })

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`)
    }

    return response.json() // Return the updated car information
  } catch (error) {
    console.error(error)
    throw new Error("Car could not be edited")
  }
}

export async function createCar(carData: CarFormData): Promise<CarCreated> {
  const url = `/.netlify/functions/create_car` // URL for your Netlify function to create a car

  try {
    const { images, portadaUrl, nombre, celular, email, ...otherData } = carData

    if (!images || images.length === 0) {
      throw new Error("No images provided")
    }

    const newImages = images.map((imgObject) => imgObject.file)

    // Step 1: Upload image files to Google Drive via your Netlify function
    const uploaded = await uploadFiles(newImages, carData._id!.toString())

    let clienteFound = await getClientes(1, 10, { celular })
    if (clienteFound.results.length === 0) {
      const clientePayload = createClientePayload({ nombre, celular, email })
      clienteFound = await createCliente(clientePayload)
    } else {
      clienteFound = await editCliente(clienteFound.results[0]._id.toString(), {
        nombre,
        celular,
        email,
      })
    }
    const uploadedFilesUrl = uploaded.files.map((file) => file.publicUrl)

    // Conver portadaUrl to a number
    const portadaIndex = parseInt(portadaUrl || "0", 10)

    const finalPortadaUrl = uploadedFilesUrl[portadaIndex]

    // Step 2: Replace image files with public URLs in the payload
    const payload = {
      ...otherData,
      folderId: uploaded.folderId,
      clienteId: clienteFound.results
        ? clienteFound.results[0]._id
        : clienteFound._id,
      portadaUrl: finalPortadaUrl,
      images: uploadedFilesUrl,
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload), // Pass the car data to create
    })

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`)
    }

    return response.json() // Return the created car information
  } catch (error) {
    console.error(error)
    throw new Error("Car could not be created")
  }
}

export async function deleteCar(id: string, folderId: string) {
  const url = `/.netlify/functions/delete_car?id=${id}` // URL for your Netlify function to delete a car
  await deleteFolder(folderId)
  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`)
    }

    return response.json() // Return the deleted car information
  } catch (error) {
    console.error(error)
    throw new Error("Car could not be deleted")
  }
}

export async function getDistinctYears() {
  try {
    const response = await fetch(
      "/.netlify/functions/get_distinct?distinct=año"
    )
    return response.json()
  } catch (error) {
    console.error(error)
    throw new Error("Cars could no be loaded")
  }
}

export async function getStats() {
  try {
    const response = await fetch("/.netlify/functions/stats");

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to fetch stats:", error);
    throw new Error("Statistics could not be loaded");
  }
}